"use client"

import { useMemo } from "react"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"

interface GenerativeAvatarProps {
  name: string
  type: "team" | "repository"
  organizationName?: string // For repositories, to determine border color
  className?: string
  size?: "sm" | "md" | "lg" | number // Accept either preset sizes or pixel size
}

/**
 * Generates a stable, deterministic hash from a string
 */
function hashString(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash)
}

/**
 * Generates deterministic HSL colors based on a string
 */
function generateColors(name: string, count: number): string[] {
  const hash = hashString(name)
  const colors: string[] = []

  for (let i = 0; i < count; i++) {
    const hue = (hash + i * 137) % 360 // Golden angle for better distribution
    const saturation = 60 + ((hash + i * 50) % 30) // 60-90%
    const lightness = 50 + ((hash + i * 20) % 20) // 50-70%
    colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`)
  }

  return colors
}

/**
 * Generates a mesh gradient SVG background
 */
function generateMeshGradient(name: string): string {
  const colors = generateColors(name, 4)
  const hash = hashString(name)

  // Generate random-looking but deterministic positions
  const positions = [
    { x: 10 + (hash % 20), y: 10 + ((hash >> 2) % 20) },
    { x: 70 + (hash % 20), y: 10 + ((hash >> 4) % 20) },
    { x: 10 + (hash % 20), y: 70 + ((hash >> 6) % 20) },
    { x: 70 + (hash % 20), y: 70 + ((hash >> 8) % 20) },
  ]

  return `
    <svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="grad1-${hash}" cx="${positions[0].x}%" cy="${positions[0].y}%">
          <stop offset="0%" style="stop-color:${colors[0]};stop-opacity:1" />
          <stop offset="100%" style="stop-color:${colors[0]};stop-opacity:0" />
        </radialGradient>
        <radialGradient id="grad2-${hash}" cx="${positions[1].x}%" cy="${positions[1].y}%">
          <stop offset="0%" style="stop-color:${colors[1]};stop-opacity:1" />
          <stop offset="100%" style="stop-color:${colors[1]};stop-opacity:0" />
        </radialGradient>
        <radialGradient id="grad3-${hash}" cx="${positions[2].x}%" cy="${positions[2].y}%">
          <stop offset="0%" style="stop-color:${colors[2]};stop-opacity:1" />
          <stop offset="100%" style="stop-color:${colors[2]};stop-opacity:0" />
        </radialGradient>
        <radialGradient id="grad4-${hash}" cx="${positions[3].x}%" cy="${positions[3].y}%">
          <stop offset="0%" style="stop-color:${colors[3]};stop-opacity:1" />
          <stop offset="100%" style="stop-color:${colors[3]};stop-opacity:0" />
        </radialGradient>
      </defs>
      <rect width="100" height="100" fill="${colors[0]}" opacity="0.3"/>
      <circle cx="${positions[0].x}" cy="${positions[0].y}" r="40" fill="url(#grad1-${hash})" />
      <circle cx="${positions[1].x}" cy="${positions[1].y}" r="40" fill="url(#grad2-${hash})" />
      <circle cx="${positions[2].x}" cy="${positions[2].y}" r="40" fill="url(#grad3-${hash})" />
      <circle cx="${positions[3].x}" cy="${positions[3].y}" r="40" fill="url(#grad4-${hash})" />
    </svg>
  `
}

/**
 * GenerativeAvatar component that creates stable, unique avatars for teams and repositories
 */
export function GenerativeAvatar({
  name,
  type,
  organizationName,
  className,
  size = "md",
}: GenerativeAvatarProps) {
  const sizeClasses = {
    sm: "h-6 w-6 text-[10px]",
    md: "h-8 w-8 text-xs",
    lg: "h-12 w-12 text-sm",
  }

  // Handle numeric size
  const isNumericSize = typeof size === "number"
  const sizeStyle = isNumericSize ? { width: `${size}px`, height: `${size}px` } : undefined
  const fontSize = isNumericSize ? `${Math.max(8, size * 0.4)}px` : undefined

  // Generate stable gradient background
  const gradientSvg = useMemo(() => generateMeshGradient(name), [name])
  const backgroundImage = useMemo(
    () => `url('data:image/svg+xml;base64,${btoa(gradientSvg)}')`,
    [gradientSvg],
  )

  // For repositories, generate border color from organization name
  const borderColor = useMemo(() => {
    if (type === "repository" && organizationName) {
      const colors = generateColors(organizationName, 1)
      return colors[0]
    }
    return undefined
  }, [type, organizationName])

  // Get unique label for fallback text
  // Uses a deterministic hash-based approach to ensure uniqueness across all names
  const label = useMemo(() => {
    const hash = hashString(name)
    const words = name.split(/[\s-_/]/).filter(Boolean)

    // Base36 character set (0-9, A-Z) for readable hash representation
    const charset = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ"

    // Generate a unique 3-character label using the hash
    // This provides 36^3 = 46,656 unique combinations
    const hashChar2 = charset[Math.floor(hash / 36) % 36]
    const hashChar3 = charset[Math.floor(hash / (36 * 36)) % 36]

    // Try to incorporate meaningful characters from the name for better recognition
    let firstChar = ""
    if (words.length >= 2) {
      // For multi-word names, use first char of first word
      firstChar = words[0][0].toUpperCase()
    } else if (words.length === 1 && words[0].length >= 1) {
      // For single-word names, use first character
      firstChar = words[0][0].toUpperCase()
    } else {
      // Fallback: use first character of the entire name
      firstChar = name[0]?.toUpperCase() || "X"
    }

    // Combine: first meaningful char + 2 hash-based chars for uniqueness
    return firstChar + hashChar2 + hashChar3
  }, [name])

  return (
    <Avatar
      className={cn(!isNumericSize && sizeClasses[size], className)}
      style={{
        ...(isNumericSize && sizeStyle),
        border: borderColor ? `2px solid ${borderColor}` : undefined,
      }}
      data-testid="generative-avatar"
    >
      <AvatarFallback
        className="font-semibold text-white"
        style={{
          backgroundImage,
          backgroundSize: "cover",
          backgroundPosition: "center",
          ...(fontSize && { fontSize }),
        }}
        data-testid="avatar-label"
      >
        {label}
      </AvatarFallback>
    </Avatar>
  )
}

GenerativeAvatar.displayName = "GenerativeAvatar"
